import type { ReactNode } from "react";

import { TONE_GLYPH, type Tone } from "./tone";

// ---------------------------------------------------------------------------
// AN EXPLANATION, NOT A STATUS.
//
// ★ WHY THIS EXISTS, 29 August 2026. `StatusChip` was doing two jobs. It is a
// PILL — a short word beside a thing, "Live", "Needs attention" — and twenty-one
// call sites were passing it a SENTENCE, because an explanatory message beside a
// broken control is a chip-shaped thing to reach for.
//
// It showed up as a layout bug and it was never really one. `.chip` is
// `white-space: nowrap`, correctly: "Needs attention" must not break over two
// lines. So a chip carrying "The list of grounds could not be read, so this form
// cannot be used right now…" measured 785px wide inside a 375px phone, and the
// WORKER APP scrolled sideways. That was patched by letting a chip wrap; this is
// the thing the patch was standing in for.
//
// ---------------------------------------------------------------------------
// IT SHARES THE TONE VOCABULARY AND DOES NOT FORK IT
//
// The same four tones, the same four glyphs, from `tone.ts`. A second set of
// colours for messages would be a second vocabulary that has to agree with the
// first, and `verify-design-parity.sh` checks the binding of exactly one.
//
// The glyph is `aria-hidden` and the words are the accessible name, which is
// `StatusChip`'s ruling for `StatusChip`'s reason: a screen reader announcing
// "warning triangle" before the sentence says the same thing twice, the second
// time in the design system's vocabulary rather than the reader's.
//
// NO EMPTY NOTICE, for `statusChip`'s reason — a message conveyed by colour and
// an icon alone is the defect the whole vocabulary exists to prevent. It throws
// where it is written rather than rendering something that still looks like a
// working interface.
// ---------------------------------------------------------------------------
export function Notice({ tone, children }: { tone: Tone; children: ReactNode }) {
  if (children === null || children === undefined || children === "") {
    throw new Error("a notice must carry words, never colour and an icon alone");
  }
  return (
    <p className={`notice notice-${tone}`}>
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d={TONE_GLYPH[tone]} fillRule="evenodd" />
      </svg>
      <span>{children}</span>
    </p>
  );
}
